import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, 
  Card, 
  Button, 
  Tag, 
  Space,
  Modal,
  message,
  Row,
  Col,
  Statistic,
  Input,
  Form,
  Tooltip,
  InputNumber,
  Select,
  Alert,
  Descriptions,
  Divider,
  Typography,
  Spin
} from 'antd';
import { 
  GiftOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  WalletOutlined,
  CreditCardOutlined,
  ThunderboltOutlined,
  BarChartOutlined,
  QrcodeOutlined
} from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { agencyVoucherService } from '../../services/agencyVoucherService';
import { AgencyVoucher, VoucherAgencyFilters, VoucherStatus } from '../../types/agency.types';
import TableFilter, { FilterConfig } from '../../components/TableFilter/TableFilter';
import moment from 'moment';
import { voucherService } from 'services/voucherService';
import { packService } from 'services/packService';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import StripeCheckoutForm from 'components/payments/StripeCheckoutForm';
import { stripeService } from 'services/stripeService';
import {FEATURE_STRIPE} from '../../appConfig';

const { Text } = Typography;

interface PackModel {
  id: number;
  name: string;
  agencyAmount: number | null;
   
}

interface VoucherFormValues {
  packId: number;
 // description: string;
  quantity: number;
  
}

type PaymentMethod = 'balance' | 'card';

const areFiltersEqual = (
  a: VoucherAgencyFilters,
  b: VoucherAgencyFilters
) => {
  return (
    a.token === b.token &&
    a.status === b.status &&
    a.amountMin === b.amountMin &&
    a.amountMax === b.amountMax
  );
};

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_live_51RALVIJdaH5TMNaHtPCbNAya0IPskKnyPx9D7e8ZUmgBJQG6ZXBGcDp1ifgmPVG9iI0ikRUmQZWXm4MxcfE3OoB600txHNevf4');

const AgencyVouchers: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [vouchers, setVouchers] = useState<AgencyVoucher[]>([]);
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState<AgencyVoucher | null>(null);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const [packs, setPacks] = useState<PackModel[]>([]);
  const [packsLoading, setPacksLoading] = useState(false);
  const [filters, setFilters] = useState<VoucherAgencyFilters>({});
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  const [stripePaymentId, setStripePaymentId] = useState<string | null>(null);
  const [showStripeForm, setShowStripeForm] = useState(false);

  const [selectedPackId, setSelectedPackId] = useState<number | null>(null);
  const [selectedQuantity, setSelectedQuantity] = useState<number>(0);

  // Activate voucher (QR code)
  const [activateLoading, setActivateLoading] = useState(false);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [activatedVoucher, setActivatedVoucher] = useState<any>(null);

  // Consumption
  const [consumptionLoading, setConsumptionLoading] = useState(false);
  const [consumptionModalVisible, setConsumptionModalVisible] = useState(false);
  const [consumptionData, setConsumptionData] = useState<any>(null);

  const [form] = Form.useForm();
  const [stats, setStats] = useState({
    totalVouchers: 0,
    activeVouchers: 0,
    usedVouchers: 0,
    totalValue: 0
  });

  const filterConfig: FilterConfig[] = [
    {
      key: 'token',
      type: 'text',
      label: t('agency.vouchers.voucherCode'),
      placeholder: t('filters.searchByCode'),
      width: 8,
    },
    {
      key: 'status',
      type: 'select',
      label: t('common.status'),
      options: [
        { label: t('status.active'), value: 'ACTIVE' },
        { label: t('status.redeemed'), value: 'REDEEMED' },
        { label: t('status.expired'), value: 'EXPIRED' },
        { label: t('status.revoked'), value: 'REVOKED' },
        { label: t('status.sent'), value: 'SENT' },
      ]
    },
    {
      key: 'amount',
      type: 'numberRange',
      label: t('common.amount')
    }
  ];

  useEffect(() => {
    setPacksLoading(true);
    // Charger les packs avec leurs prix complets
    packService.getAllWithNameAndId()
      .then(res => {
        // Si la réponse est paginée, extraire le contenu
        const packsData = res.data.content || res.data;
        setPacks(packsData);
      })
      .catch(err => {
        console.error('Erreur lors du chargement des packs:', err);
        message.error('Erreur lors du chargement des packs');
      })
      .finally(() => setPacksLoading(false));
  }, []);

  const fetchVouchers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await agencyVoucherService.getByAgency(filters);
      setVouchers(response.data);
      calculateStats(response.data);
    } catch (error) {
      message.error('Erreur lors du chargement des vouchers');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchVouchers();
  }, [fetchVouchers]);

  const calculateStats = (data) => {
    const stats = data.reduce((acc, curr) => ({
      totalVouchers: acc.totalVouchers + 1,
      activeVouchers: acc.activeVouchers + (curr.status === 'ACTIVE' ? 1 : 0),
      usedVouchers: acc.usedVouchers + (curr.status === 'REDEEMED' ? 1 : 0),
      totalValue: acc.totalValue + curr.amount
    }), { totalVouchers: 0, activeVouchers: 0, usedVouchers: 0, totalValue: 0 });
    
    setStats(stats);
  };

  const handleTransfer = async (values) => {
    try {
      await agencyVoucherService.transferToSubscriber(selectedVoucher.id, values.subscriberId);
      message.success('Voucher transféré avec succès');
      setTransferModalVisible(false);
      form.resetFields();
      fetchVouchers();
    } catch (error) {
      message.error('Erreur lors du transfert du voucher');
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    message.success('Code copié dans le presse-papiers');
  };

  const handleBackendFilterChange = (values: any) => {
    const hasBothAmounts =
      Array.isArray(values.amount) &&
      values.amount[0] != null &&
      values.amount[1] != null;

    const newFilters: VoucherAgencyFilters = {
      token: values.token || undefined,
      status: values.status || undefined,
      amountMin: hasBothAmounts ? values.amount[0] : undefined,
      amountMax: hasBothAmounts ? values.amount[1] : undefined,
    };

    setFilters(prev => {
      if (areFiltersEqual(prev, newFilters)) {
        return prev;
      }
      return newFilters;
    });
  };

  const handleBackendClearFilters = () => {
    setFilters({});
  };

  const handleActivate = async (voucher: AgencyVoucher) => {
    try {
      setActivateLoading(true);
      const response = await agencyVoucherService.bookVoucher(voucher.token);
      setActivatedVoucher(response.data);
      setQrModalVisible(true);
      fetchVouchers();
      message.success(t('agency.vouchers.activateSuccess'));
    } catch (error: any) {
      message.error(error?.response?.data?.message || t('agency.vouchers.activateError'));
    } finally {
      setActivateLoading(false);
    }
  };

  const handleViewConsumption = async (simSerial: string) => {
    try {
      setConsumptionLoading(true);
      setConsumptionModalVisible(true);
      const response = await agencyVoucherService.getConsumption(simSerial);
      setConsumptionData(response.data);
    } catch (error: any) {
      const statusCode = error?.response?.status;

      // Some APIs return 400 when no usage exists yet; show an empty consumption UI instead of an error.
      if (statusCode === 400) {
        setConsumptionData({
          simSerial,
          dataUsed: 0,
          dataLimit: 0,
          status: 'ACTIVE',
        });
      } else {
        message.error(error?.response?.data?.message || t('agency.vouchers.consumptionError'));
        setConsumptionModalVisible(false);
      }
    } finally {
      setConsumptionLoading(false);
    }
  };

  const handleOpenModal = () => {
    setIsModalVisible(true);
    setPaymentMethod(null);
    setShowStripeForm(false);
    form.resetFields();
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setPaymentMethod(null);
    setShowStripeForm(false);
    setStripeClientSecret(null);
    setStripePaymentId(null);
    form.resetFields();
  };

  const handleCreateVoucherBalance = async (values: VoucherFormValues) => {
    try {
      setLoading(true);
      await voucherService.createVoucherViaBalance({
        packs: [{
          packId: values.packId,
          quantity: values.quantity}],
        description: '',
      });

      message.success('Voucher(s) créé(s) avec succès');
      handleCloseModal();
      fetchVouchers();
    } catch (error) {
      console.error(error);
      message.error('Erreur lors de la création du voucher');
    } finally {
      setLoading(false);
    }
  };

  const handleInitiateCardPayment = async (values: VoucherFormValues) => {
    try {
      setLoading(true);
      
      // Trouver le pack sélectionné avec son prix complet
      const selectedPack = packs.find(p => p.id === values.packId);
      console.log(selectedPack);
      if (!selectedPack) {
        message.error('Pack non trouvé');
        console.log('Pack ID recherché:', values.packId, 'Packs disponibles:', packs);
        return;
      }
      
       //  Vérifier le prix agence
    const packPrice = selectedPack.agencyAmount;

    if (!packPrice || packPrice <= 0) {
      message.error(
        "Le prix du pack n'est pas défini. Veuillez contacter l'administrateur."
      );
      return;
    }
      
      const totalAmount = values.quantity * packPrice;
      console.log('Calcul du montant:', {
        pack: selectedPack.name,
        packPrice,
        quantity: values.quantity,
        totalAmount
      });
      
      setPaymentAmount(totalAmount);
      setSelectedPackId(values.packId);
      setSelectedQuantity(values.quantity);

      // Créer le PaymentIntent via le service
      const { clientSecret, paymentIntentId     } = await stripeService.createVoucherViaCard(totalAmount);
      console.log("paymentIntentId",paymentIntentId);
      setStripeClientSecret(clientSecret);
      setStripePaymentId(paymentIntentId);
      setShowStripeForm(true);
      
    } catch (error) {
      console.error('Erreur lors de l\'initialisation du paiement:', error);
      message.error('Erreur lors de l\'initialisation du paiement par carte');
    } finally {
      setLoading(false);
    }
  };



  // const handleStripeSuccess = async (paymentIntentId: string, id: number) => {
  //   try {
  //     setLoading(true);
      
  //     const formValues = form.getFieldsValue();
  //     const reference = `VOUCHER-${Date.now()}`;
      
  //     // Finaliser le paiement avec completeOrder
  //     await stripeService.completeOrder(paymentIntentId, id, reference);
      
  //     message.success('Paiement effectué et voucher(s) créé(s) avec succès');
  //     handleCloseModal();
  //     fetchVouchers();
      
  //   } catch (error) {
  //     console.error('Erreur lors de la finalisation:', error);
  //     message.error('Erreur lors de la finalisation du paiement');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

const handleStripeSuccess = async (paymentIntentId: string) => {
  if (!selectedPackId || !selectedQuantity) {
    message.error('Impossible de créer le voucher : pack ou quantité manquant');
    return;
  }

  try {
    setLoading(true);

    const vouchers = await stripeService.finalizeVoucherCreation(
     [{packId:selectedPackId,
      quantity:selectedQuantity}],
      paymentIntentId,
      `Voucher pour ${packs.find(p => p.id === selectedPackId)?.name || 'Pack inconnu'} - Paiement par carte`

    );

    console.log("selectedPackId",selectedPackId);
    console.log("selectedQuantity",selectedQuantity);
    console.log("paymentIntentId",paymentIntentId);

    message.success(`${vouchers.length} voucher(s) créé(s) avec succès !`);
    handleCloseModal();
    fetchVouchers();

  } catch (error: any) {
    console.error('Erreur lors de la création des vouchers:', error);
    message.error('Erreur lors de la création des vouchers après paiement');
  } finally {
    setLoading(false);
  }
};

     


  const handleStripeError = (error: any) => {
    console.error('Erreur de paiement Stripe:', error);
    message.error('Erreur lors du paiement par carte: ' + (error.message || 'Erreur inconnue'));
    setShowStripeForm(false);
  };

  const renderPaymentMethodSelection = () => (
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <h4 style={{ marginBottom: 24, fontSize: '1.5em', fontWeight: 'normal' }}>
        {t('agency.vouchers.choosePaymentMethod')}
      </h4>
      <Space size="large" direction="vertical" style={{ width: '100%' }}>
        <Button
          size="large"
          icon={<WalletOutlined />}
          onClick={() => setPaymentMethod('balance')}
          block
          style={{ height: 60 }}
          type='primary'
        >
          {t('agency.vouchers.payWithBalance')}
        </Button>
        <Button
          size="large"
          icon={<CreditCardOutlined />}
          onClick={() => setPaymentMethod('card')}
          block
          style={{ height: 60 }}
          type='primary'
        >
          {t('agency.vouchers.payWithCard')}
        </Button>
      </Space>
    </div>
  );

  const renderBalanceForm = () => (
    <Form<VoucherFormValues>
      form={form}
      layout="vertical"
      onFinish={handleCreateVoucherBalance}
    >
      <Form.Item
        name="packId"
        label={t('common.pack')}
        rules={[{ required: true, message: t('agency.vouchers.packRequired') }]}
      >
        <Select
          placeholder={t('agency.vouchers.selectPack')}
          options={packs.map(pack => ({
            label: pack.name,
            value: pack.id,
          }))}
          allowClear
        />
      </Form.Item>

{
     /* <Form.Item
        name="description"
        label="Description"
        rules={[{ required: true, message: 'La description est requise' }]}
      >
        <Input />
      </Form.Item>
      */
}
      <Form.Item
        name="quantity"
        label={t('agency.vouchers.quantity')}
        rules={[{ required: true, message: t('agency.vouchers.quantityRequired') }]}
      >
        <InputNumber min={1} style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Button onClick={() => setPaymentMethod(null)}>
            {t('common.back')}
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {t('common.create')}
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );

  const renderCardForm = () => (
    <>
      {!showStripeForm ? (
        <Form<VoucherFormValues>
          form={form}
          layout="vertical"
          onFinish={handleInitiateCardPayment}
        >
          <Form.Item
            name="packId"
            label={t('common.pack')}
            rules={[{ required: true, message: t('agency.vouchers.packRequired') }]}
          >
            <Select
              placeholder={t('agency.vouchers.selectPack')}
              options={packs.map(pack => ({
                label: pack.name,
                value: pack.id,
              }))}
              allowClear
            />
          </Form.Item>

          <Form.Item
            name="quantity"
            label={t('agency.vouchers.quantity')}
            rules={[{ required: true, message: t('agency.vouchers.quantityRequired') }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
            {
        /*  <Form.Item
            name="description"
            label="Description (optionnel)"
          >
            <Input />
          </Form.Item>
          */
        }
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Button onClick={() => setPaymentMethod(null)}>
                {t('common.back')}
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {t('agency.vouchers.continueToPayment')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      ) : (
        <>
          <Alert
            message={t('agency.vouchers.cardPaymentTitle')}
            description={`Montant à payer: ${paymentAmount.toFixed(2)} €`}
            type="info"
            style={{ marginBottom: 24 }}
            showIcon
          />
          
          <Elements stripe={stripePromise}>
            <StripeCheckoutForm
              amount={paymentAmount}
              onSuccess={handleStripeSuccess}
              onError={handleStripeError}
            />
          </Elements>
          
          <Button 
            onClick={() => setShowStripeForm(false)} 
            style={{ marginTop: 16 }}
          >
            {t('common.back')}
          </Button>
        </>
      )}
    </>
  );

  const columns = [
    {
      title: t('agency.vouchers.colCode'),
      dataIndex: 'token',
      key: 'token',
      render: (code: string) => (
        <Space>
          <span>{code}</span>
          <Tooltip title={t('common.copyCode')}>
            <Button 
              type="link" 
              icon={<CopyOutlined />} 
              onClick={() => copyToClipboard(code)}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: t('agency.vouchers.colAmount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (value: number) => `${value.toFixed(2)} €`,
    },
    {
      title: t('agency.vouchers.colPackName'),
      dataIndex: 'packName',
      key: 'packName',
    },
    {
      title: t('common.creationDate'),
      dataIndex: 'creationDateTime',
      key: 'creationDateTime',
      render: (date: string) => moment(date).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: t('common.expirationDate'),
      dataIndex: 'expirationDateTime',
      key: 'expirationDateTime',
      render: (date: string) => {
        const expDate = moment(date);
        const isExpiringSoon = moment().add(7, 'days').isAfter(expDate);
        return (
          <Tooltip title={isExpiringSoon ? t('common.expiringSoon') : ''}>
            <Space>
              <ClockCircleOutlined style={{ color: isExpiringSoon ? '#faad14' : undefined }} />
              {expDate.format('DD/MM/YYYY')}
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: VoucherStatus | string) => {
        const colorMap: Record<string, string> = {
          ACTIVE: 'green',
          REDEEMED: 'blue',
          EXPIRED: 'red',
          REVOKED: 'volcano',
          SENT: 'gold',
        };
        return <Tag color={colorMap[status]}>{status}</Tag>;
      },
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_: any, record: AgencyVoucher) => (
        <Space>
          {(record.status === 'ACTIVE') && !record.esimProfile && (
            <Tooltip title={t('agency.vouchers.activateTooltip')}>
              <Button
                type="primary"
                size="small"
                icon={<ThunderboltOutlined />}
                loading={activateLoading}
                onClick={() => handleActivate(record)}
              >
                {t('agency.vouchers.activate')}
              </Button>
            </Tooltip>
          )}
          {(record.esimProfile || record.status === 'REDEEMED' ) && (
            <>
              <Tooltip title={t('agency.vouchers.viewQrCode')}>
                <Button
                  size="small"
                  icon={<QrcodeOutlined />}
                  onClick={() => {
                    setActivatedVoucher(record);
                    setQrModalVisible(true);
                  }}
                >
                  QR
                </Button>
              </Tooltip>
              <Tooltip title={t('agency.vouchers.viewConsumption')}>
                <Button
                  size="small"
                  icon={<BarChartOutlined />}
                  onClick={() => handleViewConsumption(record.esimProfile!.simSerial)}
                >
                  {t('agency.vouchers.consumption')}
                </Button>
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Row gutter={[24, 24]}>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('agency.adminVouchers.totalVouchers')}
              value={stats.totalVouchers}
              prefix={<GiftOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('agency.adminVouchers.activeVouchers')}
              value={stats.activeVouchers}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('agency.adminVouchers.usedVouchers')}
              value={stats.usedVouchers}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('agency.adminVouchers.totalValue')}
              value={stats.totalValue}
              prefix="€"
              precision={2}
            />
          </Card>
        </Col>
      </Row>      
      
      <Card 
        title={t('agency.vouchers.title')}
        extra={
           false && ( // met true ici si tu veux le montrer plus tard
                 <Button
                   type="primary"
                   icon={<PlusOutlined />}
                   onClick={handleOpenModal}
                 >
                   {t('agency.vouchers.newVoucher')}
                 </Button>
               )
        }
      >
        <TableFilter
          filters={filterConfig}
          onFilterChange={handleBackendFilterChange}
          onClearFilters={handleBackendClearFilters}
        />

        <Table
          columns={columns}
          dataSource={vouchers}
          loading={loading}
          rowKey="id"
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Modal
        title={t('agency.vouchers.createVoucher')}
        open={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={500}
      >
        {!paymentMethod && renderPaymentMethodSelection()}
        {paymentMethod === 'balance' && renderBalanceForm()}
        {paymentMethod === 'card' && FEATURE_STRIPE && renderCardForm()}
      </Modal>

      <Modal
        title={t('agency.vouchers.transferTitle')}
        open={transferModalVisible}
        onCancel={() => {
          setTransferModalVisible(false);
          setSelectedVoucher(null);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleTransfer}
        >
          <Form.Item
            name="subscriberId"
            label={t('agency.vouchers.subscriberIdLabel')}
            rules={[{ required: true, message: t('agency.vouchers.subscriberIdRequired') }]}
          >
            <Input placeholder={t('agency.vouchers.enterSubscriberId')} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              {t('agency.vouchers.transfer')}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal QR Code eSIM */}
      <Modal
        title={<Space><QrcodeOutlined />{t('agency.vouchers.qrCodeTitle')}</Space>}
        open={qrModalVisible}
        onCancel={() => { setQrModalVisible(false); setActivatedVoucher(null); }}
        footer={[
          <Button key="close" onClick={() => { setQrModalVisible(false); setActivatedVoucher(null); }}>
            {t('common.close')}
          </Button>
        ]}
        width={480}
        centered
      >
        {activatedVoucher && (activatedVoucher.esimProfile?.activationCode || activatedVoucher.activationCode) ? (
          <div style={{ textAlign: 'center' }}>
            <QRCodeSVG
              value={ activatedVoucher.esimProfile?.activationCode ? (activatedVoucher.esimProfile?.activationCode?.includes("LPA:") ? activatedVoucher.esimProfile.activationCode : `LPA:${activatedVoucher.esimProfile.activationCode}`) : activatedVoucher.activationCode.includes("LPA:") ? activatedVoucher.activationCode : `LPA:${activatedVoucher.activationCode}` }
              size={256}
              style={{ margin: '0 auto 16px' }}
            />
            <Divider />
            <Descriptions column={1} size="small" bordered>
              {(activatedVoucher.esimProfile?.simSerial || activatedVoucher.simSerial) && (
                <Descriptions.Item label={t('agency.vouchers.simSerial')}>
                  <Text copyable>{activatedVoucher.esimProfile?.simSerial || activatedVoucher.simSerial}</Text>
                </Descriptions.Item>
              )}
              {(activatedVoucher.esimProfile?.smdpAddress || activatedVoucher.smdpAddress) && (
                <Descriptions.Item label={t('agency.vouchers.smdpAddress')}>
                  <Text copyable style={{ wordBreak: 'break-all' }}>
                    {activatedVoucher.esimProfile?.smdpAddress || activatedVoucher.smdpAddress}
                  </Text>
                </Descriptions.Item>
              )}
              {(activatedVoucher.esimProfile?.matchingId || activatedVoucher.matchingId) && (
                <Descriptions.Item label={t('agency.vouchers.matchingId')}>
                  <Text copyable>{activatedVoucher.esimProfile?.matchingId || activatedVoucher.matchingId}</Text>
                </Descriptions.Item>
              )}
              {(activatedVoucher.esimProfile?.activationCode || activatedVoucher.activationCode) && (
                <Descriptions.Item label={t('agency.vouchers.activationCode')}>
                  <Text copyable style={{ wordBreak: 'break-all', fontSize: 11 }}>
                    {activatedVoucher.esimProfile?.activationCode || activatedVoucher.activationCode}
                  </Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Text type="secondary">{t('agency.vouchers.noEsimData')}</Text>
          </div>
        )}
      </Modal>

      {/* Modal Consommation eSIM */}
      <Modal
        title={<Space><BarChartOutlined />{t('agency.vouchers.consumptionTitle')}</Space>}
        open={consumptionModalVisible}
        onCancel={() => { setConsumptionModalVisible(false); setConsumptionData(null); }}
        footer={[
          <Button key="close" onClick={() => { setConsumptionModalVisible(false); setConsumptionData(null); }}>
            {t('common.close')}
          </Button>
        ]}
        width={500}
        centered
      >
        {consumptionLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin size="large" />
          </div>
        ) : consumptionData ? (
          <Descriptions column={1} bordered size="small">
            {consumptionData.simSerial && (
              <Descriptions.Item label={t('agency.vouchers.simSerial')}>
                <Text copyable>{consumptionData.simSerial}</Text>
              </Descriptions.Item>
            )}
            {consumptionData.dataUsed !== undefined && (
              <Descriptions.Item label={t('agency.vouchers.dataUsed')}>
                {typeof consumptionData.dataUsed === 'number'
                  ? `${consumptionData.dataUsed} MB`
                  : consumptionData.dataUsed}
              </Descriptions.Item>
            )}
            {consumptionData.dataLimit !== undefined && (
              <Descriptions.Item label={t('agency.vouchers.dataLimit')}>
                {typeof consumptionData.dataLimit === 'number'
                  ? `${consumptionData.dataLimit} MB`
                  : consumptionData.dataLimit}
              </Descriptions.Item>
            )}
            {consumptionData.status && (
              <Descriptions.Item label={t('common.status')}>
                <Tag color={consumptionData.status === 'ACTIVE' ? 'green' : 'default'}>
                  {consumptionData.status}
                </Tag>
              </Descriptions.Item>
            )}
            {consumptionData.packName && (
              <Descriptions.Item label={t('agency.vouchers.colPackName')}>
                {consumptionData.packName}
              </Descriptions.Item>
            )}
            {/* Afficher les champs supplémentaires dynamiquement */}
            {Object.entries(consumptionData)
              .filter(([k]) => !['simSerial','dataUsed','dataLimit','status','packName'].includes(k))
              .map(([key, val]) => (
                <Descriptions.Item key={key} label={key}>
                  {String(val)}
                </Descriptions.Item>
              ))
            }
          </Descriptions>
        ) : (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Text type="secondary">{t('agency.vouchers.noConsumptionData')}</Text>
          </div>
        )}
      </Modal>
    </>
  );
};

export default AgencyVouchers;